/* ---------- ENUMS / REFERENCE DATA ------------------------- */

export const POSITIONS = [
  { v: 'forward', l: 'Center' },
  { v: 'wing',    l: 'Wing' },
  { v: 'defense', l: 'Defense' },
];

export const HANDS = [
  { v: 'left',  l: 'Left Shot' },
  { v: 'right', l: 'Right Shot' },
];

export const PLAYER_TYPES = [
  { v: 'sniper',       l: 'Sniper',        d: 'Goal scorer — all about the shot' },
  { v: 'playmaker',    l: 'Playmaker',     d: 'Passer with elite vision' },
  { v: 'dangler',      l: 'Dangler',       d: 'Stickhandler, dekes, creativity' },
  { v: 'powerForward', l: 'Power Forward', d: 'Physical, net-front, hard shots' },
  { v: 'twoWay',       l: 'Two-Way',       d: 'Balanced, responsible both ends' },
];

export const SHOTS = [
  { v: 'wrist',    l: 'Wrist Shot' },
  { v: 'snap',     l: 'Snap Shot' },
  { v: 'slap',     l: 'Slap Shot' },
  { v: 'backhand', l: 'Backhand' },
];

export const CURVES = {
  P28: { name: 'Open toe — sniper',       desc: 'Aggressive open toe — lifts pucks fast, perfect for quick releases.', bestFor: ['sniper', 'dangler'],             pos: ['forward', 'wing'],            shots: ['wrist', 'snap'] },
  P92: { name: 'Mid blade — all-rounder', desc: 'Wrist + snap + saucer. The most popular curve in pro hockey.',        bestFor: ['sniper', 'playmaker', 'twoWay'], pos: ['forward', 'wing', 'defense'], shots: ['wrist', 'snap', 'slap'] },
};

export const HLC_STOCK_LENGTHS = {
  senior: [
    { in: 63, label: 'Short — 63"' },
    { in: 67, label: 'Standard — 67"' },
    { in: 71, label: 'Extra Long — 71"' },
  ],
  intermediate: [
    { in: 58, label: 'Short — 58"' },
    { in: 63, label: 'Standard — 63"' },
    { in: 67, label: 'Extra Long — 67"' },
  ],
  junior: [
    { in: 48, label: '48"' },
    { in: 52, label: '52"' },
    { in: 58, label: '58"' },
  ],
};

export const HLC_FLEX_OPTIONS = {
  senior:       [70, 75, 80, 85, 95, 100, 105],
  intermediate: [50, 55, 60, 65],
  junior:       [20, 30, 40, 45, 50],
};

export const TIERS = ['junior', 'intermediate', 'senior'];

/* ---------- DISPLAY HELPERS -------------------------------- */

export function tierLabel(tier)    { return tier === 'senior' ? 'Senior' : tier === 'intermediate' ? 'Intermediate' : 'Junior'; }
export function priceForTier(tier) { return tier === 'senior' ? '$199.99' : tier === 'intermediate' ? '$185.99' : '$144.99'; }
export function formatFeet(inches) { return `${Math.floor(inches / 12)}'${inches % 12}"`; }

export const CM_PER_INCH = 2.54;
export const KG_PER_LB   = 0.45359237;
export function inchesToCm(i) { return Math.round(i * CM_PER_INCH); }
export function cmToInches(c) { return Math.round(c / CM_PER_INCH); }
export function lbsToKg(l)    { return Math.round(l * KG_PER_LB); }
export function kgToLbs(k)    { return Math.round(k / KG_PER_LB); }

/* ---------- SERVICE OBJECT --------------------------------- */

/**
 * @typedef {Object} StickFitInput
 * @property {number} heightInches
 * @property {number} weightLbs
 * @property {('forward'|'wing'|'defense')} position
 * @property {('left'|'right')} hand
 * @property {string} playerType
 * @property {string} shotType
 */

export class StickFit {
  call(input) {
    const {
      hand,
      heightInches,
      playerType,
      position,
      preferJunior,
      shotType,
      weightLbs,
    } = input;

    const r = this._calcCutAndFlex(playerType, position, heightInches, weightLbs, preferJunior);

    return {
      ...r,
      tierLabel:  tierLabel(r.tier),
      price:      priceForTier(r.tier),
      curves:     this._recommendCurve(playerType, position, shotType),
      hand,
      metric: {
        cm: inchesToCm(heightInches),
        kg: lbsToKg(weightLbs),
      },
    };
  }

  _calcCutAndFlex(playerType, position, totalInches, weightLbs, preferJunior = false) {
    const { ideal: idealLength, method: lenMethod } = this._calcIdealLength(position, totalInches);
    const { flex: bodyTargetFlex, method: flexMethod } = this._calcTargetFlex(playerType, position, weightLbs);

    if (preferJunior) {
      const maxJuniorFlex = Math.max(...HLC_FLEX_OPTIONS.junior);
      const maxJuniorLen = Math.max(...HLC_STOCK_LENGTHS.junior.map(s => s.in));
      if (idealLength <= maxJuniorLen && bodyTargetFlex <= maxJuniorFlex) {
        const result = this._bestForTier('junior', idealLength, bodyTargetFlex, flexMethod, lenMethod);
        if (result) {
          const { _score, ...rest } = result;
          return rest;
        }
      }
    }

    const startIdx = TIERS.indexOf(this._minTierByLength(idealLength));
    let best = null;

    for (let i = startIdx; i < TIERS.length; i++) {
      const candidate = this._bestForTier(TIERS[i], idealLength, bodyTargetFlex, flexMethod, lenMethod);
      if (candidate && (!best || candidate._score < best._score)) {
        best = candidate;
      }
    }

    const { _score, ...result } = best;
    return result;
  }

  _calcIdealLength(position, totalInches) {
    const ratio = position === 'defense' ? 0.895 : 0.875;
    let ideal, method;
    if      (totalInches <= 51) { ideal = 40; method = 'lookup'; }
    else if (totalInches <= 55) { ideal = 44; method = 'lookup'; }
    else if (totalInches <= 59) { ideal = 50 + Math.round((totalInches - 56) * 0.75); method = 'lookup'; }
    else if (totalInches <= 76) { ideal = Math.round((totalInches + 1.5) * ratio); method = 'formula'; }
    else if (totalInches <= 78) { ideal = 68; method = 'lookup'; }
    else                        { ideal = 70; method = 'lookup'; }
    return { ideal, method };
  }

  _calcTargetFlex(playerType, position, weightLbs) {
    let base, method;
    if      (weightLbs <  70) { base = 30;  method = 'lookup'; }
    else if (weightLbs <  85) { base = 40;  method = 'lookup'; }
    else if (weightLbs < 100) { base = 50;  method = 'lookup'; }
    else if (weightLbs <= 195) {
      base = Math.round(weightLbs / 2 / 5) * 5;
      if (position   === 'defense')      base += 5;
      if (playerType === 'sniper')       base -= 5;
      if (playerType === 'powerForward') base += 5;
      if (playerType === 'dangler')      base -= 5;
      method = 'formula';
    }
    else if (weightLbs <= 220) { base = 100; method = 'lookup'; }
    else                       { base = 110; method = 'lookup'; }

    base = Math.max(30, Math.min(110, base));
    return { flex: Math.round(base / 5) * 5, method };
  }

  _recommendCurve(playerType, position, shotType) {
    const available = ['P28', 'P92'];
    const scored = available.map(k => {
      const c = CURVES[k];
      let s = 0;
      if (c.pos.includes(position))      s += 3;
      if (c.bestFor.includes(playerType)) s += 4;
      if (c.shots.includes(shotType))    s += 2;
      return [k, s];
    });
    scored.sort((a, b) => b[1] - a[1]);
    return scored.map(p => p[0]);
  }

  _snapFlex(targetFlex, tier) {
    const options = HLC_FLEX_OPTIONS[tier];
    if (!options || options.length === 0) return targetFlex;
    return options.reduce((a, b) => (Math.abs(b - targetFlex) < Math.abs(a - targetFlex) ? b : a));
  }

  _snapToHlcLength(idealLength, tier) {
    const options = HLC_STOCK_LENGTHS[tier];
    if (!options || options.length === 0) return { in: idealLength, label: idealLength + '"' };
    const longEnough = options.filter(o => o.in >= idealLength);
    if (longEnough.length > 0) {
      return longEnough.reduce((a, b) => (a.in < b.in ? a : b));
    }
    return options.reduce((a, b) => (a.in > b.in ? a : b));
  }

  _bestForTier(tier, idealLength, bodyTargetFlex, flexMethod, lenMethod) {
    const tierFlexes = HLC_FLEX_OPTIONS[tier];
    const minF = Math.min(...tierFlexes);
    const maxF = Math.max(...tierFlexes);
    let best = null;

    for (const stock of HLC_STOCK_LENGTHS[tier]) {
      if (stock.in < idealLength) continue;

      const rawCut      = stock.in - idealLength;
      const inchesToCut = Math.round(rawCut * 2) / 2;
      const flexAdded   = Math.round(inchesToCut * 5);

      const desiredFlex  = Math.max(minF, Math.min(maxF, bodyTargetFlex - flexAdded));
      const purchaseFlex = this._snapFlex(desiredFlex, tier);
      const flexAfterCut = purchaseFlex + flexAdded;

      const score = Math.abs(flexAfterCut - bodyTargetFlex) + inchesToCut;

      if (!best || score < best.score) {
        best = {
          tier, targetFlex: bodyTargetFlex, purchaseFlex,
          inchesToCut, flexAdded, flexAfterCut,
          idealLength, standardLen: stock.in, lengthLabel: stock.label,
          flexMethod, lenMethod, score,
        };
      }
    }

    if (!best) return null;
    const { score, ...result } = best;
    return { ...result, _score: score };
  }

  _minTierByLength(idealLength) {
    if (idealLength > 63) return 'senior';
    if (idealLength > 52) return 'intermediate';
    return 'junior';
  }
}

/* ---------- UMD wrapper for plain <script> tags ------------ */
if (typeof window !== 'undefined') {
  window.HLC = window.HLC || {};
  window.HLC.stickFit = new StickFit();
  window.HLC.StickFit = {
    CURVES, HLC_FLEX_OPTIONS, HLC_STOCK_LENGTHS,
    HANDS, PLAYER_TYPES, POSITIONS, SHOTS, TIERS,
    cmToInches, formatFeet, inchesToCm, kgToLbs, lbsToKg,
    priceForTier, tierLabel,
  };
}
